import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { MutationHandlingServiceV2 } from '@akebono/core';
import {
  ProductionDateFetchGQL,
  ProductionDateFetchPayload,
} from '../../../../../graphql/service/graphql-auto-main-service';
import { map } from 'rxjs/operators';
import { Params } from '@angular/router';
import { CheckProductionDatesDataType } from '../../types/check-production-dates-data.type';
import { CheckProductionDatesParamsType } from '../../types/check-production-dates-params.type';
import { normalizeFrame } from 'src/app/utils/frame.util';

@Component({
  selector: 'app-check-production-dates-result',
  templateUrl: './check-production-dates-result.component.html',
  styleUrls: ['./check-production-dates-result.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckProductionDatesResultComponent {
  data$: Observable<CheckProductionDatesDataType[]>;
  loading$: Observable<boolean>;

  @Input() set checkProductionDateParams(value: CheckProductionDatesParamsType | null) {
    if (value?.company && value?.vin) {
      this.fetchData(value);
    }
  }

  @Output() clear = new EventEmitter<void>();

  private clearDataSubject = new BehaviorSubject<void>(undefined);

  constructor(
    private mhs: MutationHandlingServiceV2,
    private productionDateFetchGQL: ProductionDateFetchGQL,
  ) {}

  fetchData(params: Params | null) {
    if (!params) return;

    const mutationRef = this.mhs.mutate(this.productionDateFetchGQL, {
      input: { company: params.company, vin: normalizeFrame(params.vin) },
    });
    this.loading$ = mutationRef.loading;
    const mutationData$ = mutationRef.data.pipe(
      map((result) => this.compilePayloadPairs(result?.productionDateFetch ?? null)),
    );

    this.data$ = merge(mutationData$, this.clearDataSubject.pipe(map(() => null)));
  }

  private compilePayloadPairs(
    productionDatePayload: ProductionDateFetchPayload | null,
  ): CheckProductionDatesDataType[] {
    const excludePayloadFields = ['year', 'month', 'day', 'vin'];
    const payload = productionDatePayload?.data?.epc_export ?? {};

    if (productionDatePayload?.productionDate) {
      payload.productionDate = productionDatePayload.productionDate;
    } else {
      const productionDateGte = productionDatePayload?.productionDateGte;
      const productionDateLte = productionDatePayload?.productionDateLte;

      if (productionDateGte && productionDateLte) {
        payload.productionDateGte = productionDateGte;
        payload.productionDateLte = productionDateLte;
      }
    }

    return Object.entries(payload)
      .map(([key, value]) => ({ key, value }))
      .filter(({ key, value }) => !excludePayloadFields.includes(key) && Boolean(value));
  }

  hideResult(): void {
    this.clearDataSubject.next();
    this.clear.emit();
  }

  checkHasError(payload: CheckProductionDatesDataType[]): boolean {
    return payload.some((item) => item.key === 'error');
  }

  checkHasEstimatedDate(payload: CheckProductionDatesDataType[]): boolean {
    const hasGte = payload.some((item) => item.key === 'productionDateGte');
    const hasLte = payload.some((item) => item.key === 'productionDateLte');
    return hasGte && hasLte;
  }

  getDateValue(payload: CheckProductionDatesDataType[], key: string): string {
    const item = payload.find((item) => item.key === key);
    return item?.value || '';
  }
}
